"use client";

import { isServer } from "@decibeltrade/web-core/utils";
import React from "react";

import { env } from "@/env";
import { Logger } from "@/utils/logger";

import { StorageValue } from "./types";

/**
 * Get the network name for storage key prefixing.
 */
export function getNetworkForStorage(): string {
  return env.NEXT_PUBLIC_SDK_CONFIG_NAME;
}

export interface StorageKeyOptions {
  network?: string;
  chain?: string;
}

/**
 * Normalize a chain name for use in storage keys.
 * E.g., "Ethereum" -> "ethereum", "EVM Chain 42161" -> "evm-chain-42161"
 */
function normalizeChainForStorage(chain: string): string {
  return chain.toLowerCase().replace(/\s+/g, "-");
}

/**
 * Build a storage key with optional network and chain prefixes.
 * @param baseKey - The base key (e.g., "pdek-0x123...")
 * @param options - Optional network and chain prefixes
 * @returns The prefixed key, e.g. "mainnet-ethereum-pdek-0x123..."
 */
export function buildStorageKey(baseKey: string, options?: StorageKeyOptions): string {
  const parts: string[] = [];
  if (options?.network) parts.push(getNetworkForStorage());
  if (options?.chain) parts.push(normalizeChainForStorage(options.chain));
  parts.push(baseKey);
  return parts.join("-");
}

/**
 * useLocalStorage
 *
 * from: https://github.com/uidotdev/usehooks/blob/main/index.js
 *
 * the useLocalStorageSubscribe function is used to listen for changes to the localStorage item
 * whether it's from the current window or another window.
 *
 * @example
 * const [value, setValue] = useLocalStorage("my-key", "my-value");
 *
 * @param key The key to store the value under
 * @param initialValue The initial value to store
 * @returns A tuple containing the current value and a function to update the value
 */

function dispatchStorageEvent(key: string, newValue: string | null) {
  window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
}

export const setLocalStorageItem = (key: string, value: StorageValue) => {
  if (isServer()) return;
  if (typeof value === "undefined" || value === null) {
    removeLocalStorageItem(key);
    return;
  }

  const stringifiedValue = JSON.stringify(value);
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

export const removeLocalStorageItem = (key: string) => {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

export const getLocalStorageItem = (key: string) => {
  if (isServer()) return null;
  return window.localStorage.getItem(key);
};

export function getParsedLocalStorageItem(key: string): StorageValue | null {
  try {
    const item = getLocalStorageItem(key);
    if (!item) return null;
    return JSON.parse(item) as StorageValue;
  } catch {
    return null;
  }
}

export const useLocalStorageSubscribe = (callback: (event: StorageEvent) => void) => {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
};

const getLocalStorageServerSnapshot = () => {
  return null;
};

type SetState<T extends StorageValue> = (v: T | ((prevState: T) => T)) => void;
type UseLocalStorageReturn<T extends StorageValue> = [T, SetState<T>, boolean];

export function useLocalStorage<T extends StorageValue>(
  key: string,
  initialValue: T,
): UseLocalStorageReturn<T> {
  const getSnapshot = () => getLocalStorageItem(key);

  const store = React.useSyncExternalStore(
    useLocalStorageSubscribe,
    getSnapshot,
    getLocalStorageServerSnapshot,
  );

  const setState = React.useCallback(
    (v: StorageValue | ((prevState: StorageValue) => StorageValue)) => {
      try {
        // Always read the current value from localStorage directly to avoid stale closures
        const currentStorageValue = getLocalStorageItem(key);
        const currentValue: StorageValue = currentStorageValue
          ? (JSON.parse(currentStorageValue) as StorageValue)
          : initialValue;

        const nextState =
          typeof v === "function"
            ? (v as (prevState: StorageValue) => StorageValue)(currentValue)
            : v;

        if (nextState === null) {
          removeLocalStorageItem(key);
        } else {
          setLocalStorageItem(key, nextState);
        }
      } catch (error) {
        Logger.error(error, {
          message: "Failed to set local storage item",
          key,
          value: v,
        });
      }
    },
    [key, initialValue],
  );

  React.useEffect(() => {
    if (getLocalStorageItem(key) === null && typeof initialValue !== "undefined") {
      setLocalStorageItem(key, initialValue);
    }
  }, [key, initialValue]);

  const parsedStore = React.useMemo(
    () => (store ? (JSON.parse(store) as T) : initialValue),
    [store, initialValue],
  );

  return [parsedStore, setState, store === null] as UseLocalStorageReturn<T>;
}
